import React, { useEffect, useContext } from "react";
import EditEscrowModal from "../../popups/EditEscrowModal";
import ControlledField from "./ControlledField";
import { ThemeContext } from "../../../../context/ThemeContext";
import {
  calculateDiscount,
  computeClosingCostTotals,
} from "../utils/calculations";
import { applyWaSalesTaxClosingCosts as mergeWaSalesTaxClosingCosts } from "../../buyer/utils/waSalesTaxClosingCosts";

const REFI_WA_MAIN_ID = 5;
const REFI_WA_LENDER_TAX_ROW = 6;
const REFI_WA_ESCROW_TAX_ROW = 7;

export default function EscrowTitleFields({
  control,
  formData,
  isInitialLoad,
  openModal,
  closeModal,
  FloatingNumber,
  cleanNumber,
  handleCurrencyBlur,
  setFormData,
  setValue,
  onFormatted,
  EscrowDisabled,
  onContinue,
}) {
  const theme = useContext(ThemeContext);
  const mainId = theme?.id ?? "";

  const titleMeta = formData?.titleSettlementMeta ?? {};

  const prevDiscountRef = React.useRef(titleMeta.discount);
  const prevDiscountAmountRef = React.useRef(titleMeta.discountAmount);

  useEffect(() => {
    if (isInitialLoad) return;

    let isDiscountChanged = true;
    if (prevDiscountRef.current !== titleMeta.discount) {
      isDiscountChanged = true;
    } else if (prevDiscountAmountRef.current !== titleMeta.discountAmount) {
      isDiscountChanged = false;
    }

    const getDiscountdata = calculateDiscount(formData, isDiscountChanged);

    setFormData((prev) => ({
      ...prev,
      titleSettlementMeta: {
        ...prev.titleSettlementMeta,
        discount: getDiscountdata.discountPercent,
        discountAmount: getDiscountdata.discountAmount,
      },
    }));

    prevDiscountRef.current = titleMeta.discount;
    prevDiscountAmountRef.current = titleMeta.discountAmount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleMeta.discount, titleMeta.discountAmount]);

  const syncClosingCostRowToRhf = (index, amount) => {
    if (typeof setValue !== "function") return;
    const n = Number(amount) || 0;
    const s = n.toFixed(2);
    const base = `closingCostsMeta.${index}`;
    setValue(`${base}.value`, s, { shouldDirty: true, shouldTouch: true });
    setValue(`${base}.total`, s, { shouldDirty: true, shouldTouch: true });
    setValue(`${base}.type`, "Flat Fee", {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const parseAmount = (value) => {
    const normalized = String(value ?? "").replace(/,/g, "");
    return Number(normalized) || 0;
  };

  const applyWaSalesTaxClosingCosts = (
    prev,
    { escrowSettlementAmount, lendersTitleAmount } = {},
  ) => {
    const state = prev?.addressMeta?.state;
    const salesTaxRate = Number(prev?.titleSettlementMeta?.salesTaxRate) || 0;
    if (
      Number(mainId) !== REFI_WA_MAIN_ID ||
      state !== "WA" ||
      salesTaxRate === 0
    ) {
      return prev;
    }

    const escrowAmt = Number(escrowSettlementAmount) || 0;
    const lenderAmt = Number(lendersTitleAmount) || 0;
    const taxLender = lenderAmt * salesTaxRate;
    const taxEscrow = escrowAmt * salesTaxRate;

    const next = mergeWaSalesTaxClosingCosts(prev, {
      escrowSettlementAmount,
      lendersTitleAmount,
      salesTaxRate,
      lenderRowIndex: REFI_WA_LENDER_TAX_ROW,
      escrowTaxRowIndex: REFI_WA_ESCROW_TAX_ROW,
    });

    syncClosingCostRowToRhf(REFI_WA_LENDER_TAX_ROW, taxLender);
    syncClosingCostRowToRhf(REFI_WA_ESCROW_TAX_ROW, taxEscrow);

    return computeClosingCostTotals(next);
  };

  const taxBaseFromTitleMeta = (meta) =>
    parseAmount(meta?.ownersTitleAmount ?? meta?.lendersTitleAmount);

  return (
    <>
      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <label className="lbl-refi-section style-m-2">
            Escrow or Settlement
          </label>
        </div>
        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group sign-of-amount no-m-space">
              <ControlledField
                name="titleSettlementMeta.escrowSettlementAmount"
                control={control}
                InputComponent={FloatingNumber}
                placeholder="0.00"
                type="text"
                defaultValue={titleMeta.escrowSettlementAmount ?? "0.00"}
                value={titleMeta.escrowSettlementAmount ?? "0.00"}
                cleanNumber={cleanNumber}
                handleCurrencyBlur={handleCurrencyBlur}
                inputProps={{
                  disabled: EscrowDisabled.escrowSettlement,
                  className:
                    EscrowDisabled?.escrowSettlement === false
                      ? "premium-border"
                      : "",
                }}
                onFormatted={(fieldName, formatted) => {
                  setFormData((prev) => {
                    const prevTitle = prev?.titleSettlementMeta ?? {};
                    const updated = {
                      ...prev,
                      phase: "titleSettlementMeta",
                      titleSettlementMeta: {
                        ...prevTitle,
                        escrowSettlementAmount: formatted,
                      },
                    };
                    return applyWaSalesTaxClosingCosts(updated, {
                      escrowSettlementAmount: parseAmount(formatted),
                      lendersTitleAmount: taxBaseFromTitleMeta(prevTitle),
                    });
                  });
                  setValue?.(
                    "titleSettlementMeta.escrowSettlementAmount",
                    formatted,
                    { shouldDirty: true, shouldTouch: true },
                  );
                }}
              />
            </div>
            <div className="calc-frm-fld form-group no-m-space edit-container">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    title: "Edit Escrow / Settlement Fee",
                    size: "smallmodel",
                    content: (
                      <EditEscrowModal
                        description="Escrow Title Policy"
                        onClose={closeModal}
                        onContinue={(setting) =>
                          onContinue({ escrowSettlement: setting })
                        }
                      />
                    ),
                  });
                }}
              >
                <i className="icon-edit" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <label className="lbl-refi-section style-m-2">Title Insurance</label>
        </div>
        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group sign-of-amount no-m-space">
              <ControlledField
                name="titleSettlementMeta.ownersTitleAmount"
                control={control}
                InputComponent={FloatingNumber}
                placeholder="0.00"
                type="text"
                defaultValue={titleMeta.ownersTitleAmount ?? "0.00"}
                value={titleMeta.ownersTitleAmount ?? "0.00"}
                cleanNumber={cleanNumber}
                handleCurrencyBlur={handleCurrencyBlur}
                inputProps={{
                  disabled: EscrowDisabled.ownersPolicy,
                  className:
                    EscrowDisabled?.ownersPolicy === false
                      ? "premium-border"
                      : "",
                }}
                onFormatted={(fieldName, formatted) => {
                  setFormData((prev) => {
                    const prevTitle = prev?.titleSettlementMeta ?? {};
                    const updated = {
                      ...prev,
                      phase: "titleSettlementMeta",
                      titleSettlementMeta: {
                        ...prevTitle,
                        ownersTitleAmount: formatted,
                      },
                    };
                    return applyWaSalesTaxClosingCosts(updated, {
                      escrowSettlementAmount: parseAmount(
                        updated.titleSettlementMeta?.escrowSettlementAmount,
                      ),
                      lendersTitleAmount: parseAmount(formatted),
                    });
                  });
                  setValue?.(
                    "titleSettlementMeta.ownersTitleAmount",
                    formatted,
                    { shouldDirty: true, shouldTouch: true },
                  );
                }}
              />
            </div>
            <div className="calc-frm-fld form-group no-m-space edit-container">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    title: "Edit Title Insurance",
                    size: "smallmodel",
                    content: (
                      <EditEscrowModal
                        description="Title Insurance"
                        onClose={closeModal}
                        onContinue={(setting) =>
                          onContinue({ ownersPolicy: setting })
                        }
                      />
                    ),
                  });
                }}
              >
                <i className="icon-edit" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* <div className="calc-frm-field">
        <div className="calc-frm-fld form-group">
          <label className="lbl-refi-section style-m-2">
            Lender&apos;s Title
          </label>
        </div>
        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group sign-of-amount no-m-space">
              <ControlledField
                name="titleSettlementMeta.lendersTitleAmount"
                control={control}
                InputComponent={FloatingNumber}
                placeholder="0.00"
                type="text"
                defaultValue={titleMeta.lendersTitleAmount ?? "0.00"}
                value={titleMeta.lendersTitleAmount ?? "0.00"}
                cleanNumber={cleanNumber}
                handleCurrencyBlur={handleCurrencyBlur}
                inputProps={{
                  disabled: EscrowDisabled.lendersPolicy,
                  className:
                    EscrowDisabled?.lendersPolicy === false
                      ? "premium-border"
                      : "",
                }}
                onFormatted={(fieldName, formatted) => {
                  setFormData((prev) => ({
                    ...prev,
                    phase: "titleSettlementMeta",
                    titleSettlementMeta: {
                      ...prev.titleSettlementMeta,
                      lendersTitleAmount: formatted,
                    },
                  }));
                  setValue?.(
                    "titleSettlementMeta.lendersTitleAmount",
                    formatted,
                    { shouldDirty: true, shouldTouch: true },
                  );
                }}
              />
            </div>
            <div className="calc-frm-fld form-group no-m-space edit-container">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    title: "Edit Lender's Title",
                    size: "smallmodel",
                    content: (
                      <EditEscrowModal
                        description="Lender's Title Policy"
                        onClose={closeModal}
                        onContinue={(setting) =>
                          onContinue({ lendersPolicy: setting })
                        }
                      />
                    ),
                  });
                }}
              >
                <i className="icon-edit" />
              </button>
            </div>
          </div>
        </div>
      </div> */}

      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group sign-of-percentage">
          <ControlledField
            name="titleSettlementMeta.discount"
            label="Discount %"
            control={control}
            InputComponent={FloatingNumber}
            placeholder="0.00"
            type="text"
            defaultValue={titleMeta.discount ?? "0.00"}
            value={
              titleMeta.discount != null ? String(titleMeta.discount) : "0.00"
            }
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
            onFormatted={(fieldName, formatted) =>
              onFormatted("discount", formatted)
            }
          />
        </div>

        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group no-m-space sign-of-amount">
              <ControlledField
                name="titleSettlementMeta.discountAmount"
                control={control}
                InputComponent={FloatingNumber}
                placeholder="0.00"
                type="text"
                defaultValue={titleMeta.discountAmount ?? "0.00"}
                value={
                  titleMeta.discountAmount != null
                    ? String(titleMeta.discountAmount)
                    : "0.00"
                }
                cleanNumber={cleanNumber}
                handleCurrencyBlur={handleCurrencyBlur}
                onFormatted={(fieldName, formatted) =>
                  onFormatted("discountAmount", formatted)
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
